import { useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { commonComponentsRegistry } from '../../registry/CommonRegistry';
import { useForm, useWatch } from 'react-hook-form';
import { Utils } from 'jamespot-react-components';
import { v4 as uuid } from 'uuid';
import { Platform, Toast } from 'jamespot-front-business';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import styled from 'styled-components';
import { padStartTwoDigits } from 'jamespot-user-api';

const ModalForm = commonComponentsRegistry.getLazyComponent('ModalForm');
const InputDateTime = commonComponentsRegistry.getLazyComponent('InputDateTime');
const InputTextRaw = commonComponentsRegistry.getLazyComponent('InputTextRaw');
const FlexBox = commonComponentsRegistry.getLazyComponent('FlexBox');
const IconButton = commonComponentsRegistry.getLazyComponent('IconButton');

type ScheduleData = {
    scheduleDate: string;
    permalinkId: string;
};

export type ModalScheduleProps = {
    open: boolean;
    closeHandler: () => void;
    onSubmit: (data: ScheduleData) => void;
};

const IconButtonWrapper = styled.div`
    margin-bottom: ${({ theme }) => theme.space.xs}px;
`;

export const ModalSchedule = ({ open, closeHandler, onSubmit }: ModalScheduleProps) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const urlBase = useAppSelector(Platform.selectors.selectUrlBase);
    const form = useForm<ScheduleData>({
        defaultValues: async () => {
            const now = new Date();
            return {
                scheduleDate: `${Utils.date.formatDate(now)} ${padStartTwoDigits(now.getHours())}:${padStartTwoDigits(now.getMinutes())}`,
                permalinkId: uuid(),
            };
        },
    });

    const onSubmitHandler = form.handleSubmit((data) => {
        onSubmit(data);
    });

    useEffect(() => {
        if (open) {
            form.reset();
        }
    }, [form, open]);

    const permalinkId = useWatch({ control: form.control, name: 'permalinkId' });
    const permalink = useMemo(() => {
        return `${urlBase}permalink/${permalinkId}`;
    }, [permalinkId, urlBase]);

    return (
        <ModalForm
            open={open}
            closeHandler={closeHandler}
            onSubmit={onSubmitHandler}
            maxHeight={'620px'}
            title={intl.formatMessage({ id: 'GLOBAL_Schedule' })}
            buttons={[
                {
                    children: <FormattedMessage id="GLOBAL_Cancel" />,
                    color: 'cancel',
                    clickHandler: () => closeHandler(),
                },
                {
                    children: <FormattedMessage id="GLOBAL_Schedule" />,
                    color: 'valid',
                    clickHandler: () => {
                        closeHandler();
                        onSubmitHandler();
                    },
                    disabled: !form.watch('scheduleDate'),
                },
            ]}>
            <InputDateTime
                rules={{ required: true }}
                label={'GLOBAL_Scheduled_Date'}
                control={form.control}
                name={'scheduleDate'}
            />
            <FlexBox y={'flex-end'}>
                <InputTextRaw
                    label={intl.formatMessage({ id: 'GLOBAL_Permalink' })}
                    value={permalink}
                    onChange={() => null}
                    margin={'0'}
                    width={'100%'}
                />
                <IconButtonWrapper>
                    <IconButton
                        srOnly={'GLOBAL_Copy_Link'}
                        icon={'icon-copy'}
                        colorVariant={'primary'}
                        onClick={() => {
                            navigator.clipboard.writeText(permalink).then(() => {
                                dispatch(
                                    Toast.actions.success({
                                        label: intl.formatMessage({ id: 'CLIPBOARD_Copy_Success' }),
                                    }),
                                );
                            });
                        }}
                    />
                </IconButtonWrapper>
            </FlexBox>
        </ModalForm>
    );
};
